import { useMemo } from 'react'

import { Period } from '~/app/accounting/period-utils'
import { useAccountingTimezone } from '~/hooks/useAccountingTimezone'
import {
  addMonths,
  addYears,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  startOfMonth,
  startOfQuarter,
  startOfYear,
} from '~/utils/timezone'

export const CUSTOM_PERIOD_ID = 'custom'

export type ReportPeriodOption = Omit<Period, 'startDate' | 'endDate'> & {
  id: string
  startDate: Date
  endDate: Date
}

const quarterLabel = (date: Date, tz: 'utc' | 'local') => {
  const month = tz === 'utc' ? date.getUTCMonth() : date.getMonth()
  const year = tz === 'utc' ? date.getUTCFullYear() : date.getFullYear()
  return `Q${Math.floor(month / 3) + 1} ${year}`
}

const monthLabel = (date: Date, tz: 'utc' | 'local') =>
  new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: tz === 'utc' ? 'UTC' : undefined,
  }).format(date)

const yearLabel = (date: Date, tz: 'utc' | 'local') =>
  String(tz === 'utc' ? date.getUTCFullYear() : date.getFullYear())

/**
 * Closed calendar periods, because a report is something you hand an
 * accountant after the period ends. The tab filters offer running periods
 * ("this month to date"); reports deliberately don't.
 */
export const getReportPeriods = (tz: 'utc' | 'local'): ReportPeriodOption[] => {
  const now = new Date()
  const lastMonth = addMonths(now, -1, tz)
  const twoMonthsAgo = addMonths(now, -2, tz)
  const lastQuarter = addMonths(startOfQuarter(now, tz), -1, tz)
  const lastYear = addYears(now, -1, tz)

  return [
    {
      id: 'last-month',
      label: monthLabel(lastMonth, tz),
      startDate: startOfMonth(lastMonth, tz),
      endDate: endOfMonth(lastMonth, tz),
    },
    {
      id: 'two-months-ago',
      label: monthLabel(twoMonthsAgo, tz),
      startDate: startOfMonth(twoMonthsAgo, tz),
      endDate: endOfMonth(twoMonthsAgo, tz),
    },
    {
      id: 'last-quarter',
      label: quarterLabel(lastQuarter, tz),
      startDate: startOfQuarter(lastQuarter, tz),
      endDate: endOfQuarter(lastQuarter, tz),
    },
    {
      id: 'this-year',
      label: `${yearLabel(now, tz)} to date`,
      startDate: startOfYear(now, tz),
      endDate: now,
    },
    {
      id: 'last-year',
      label: yearLabel(lastYear, tz),
      startDate: startOfYear(lastYear, tz),
      endDate: endOfYear(lastYear, tz),
    },
  ]
}

export const useReportPeriods = (
  tzOverride?: 'utc' | 'local',
): ReportPeriodOption[] => {
  const [committedTz] = useAccountingTimezone()
  const tz = tzOverride ?? committedTz

  return useMemo(() => getReportPeriods(tz), [tz])
}
